import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, OptionCardInput, Space, Spinner, Modal } from '@ag1/ui';
import { pluralize } from 'shared/utils/pluralize';
import { useLanguageValue } from 'state/hooks/useLanguage';
import { ShortLocaleEnum } from 'schema/locale';
import { useEffect, useState } from 'react';
import { DeliveryFrequencyInfo } from '../DeliveryFrequencyInfo';
import { DeliveryFrequencyRibbon } from '../DeliveryFrequencyRibbon';
import { type DeliveryFrequencyViewProps } from '../../DeliveryFrequencyV2.types';
import useDeliveryFrequencyModalV2 from '../../hooks/useDeliveryFrequencyModalV2';
import { CHANGE_FREQUENCY_STEP } from '../../constants';
import styles from './DeliveryFrequencyView.module.scss';

const DeliveryFrequencyView = ({
  isLoadingStatus,
  handleSubmitInternal,
  intervalFrequencyList,
  control,
  isSubmitting,
  isDisabled,
  frequencyUnit,
  currentInterval,
  newDateValue
}: DeliveryFrequencyViewProps) => {
  const { t } = useTranslation('modalWindows');
  const { setStep } = useDeliveryFrequencyModalV2();
  const language = useLanguageValue();

  const [selectedNewFrequency, setSelectedNewFrequency] = useState(0);

  useEffect(() => {
    setSelectedNewFrequency(Number(currentInterval));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Modal.Header>{t('editFrequency.titleView')}</Modal.Header>
      <Modal.Content hasFooter className={styles.modalContent}>
        <form onSubmit={handleSubmitInternal} className={styles.form}>
          {intervalFrequencyList.map((option: string, index: number) => {
            const isFirstElement = index === 0;
            const label = `${t('editFrequency.item')} ${
              language === ShortLocaleEnum.de
                ? `${option} ${frequencyUnit}`
                : pluralize(Number(option), frequencyUnit)
            }`;

            return (
              <Space
                key={option}
                top={{ xxs: isFirstElement ? 0 : 12, s: isFirstElement ? 0 : 16 }}>
                <Controller
                  control={control}
                  name="deliveryOptions"
                  render={({ field: { onChange, name } }) => (
                    <OptionCardInput
                      disabled={isSubmitting || isLoadingStatus}
                      id={`frequency-${option}-days`}
                      name={name}
                      value={option}
                      type="radio"
                      label={label}
                      onChange={(event) => {
                        onChange(event);
                        setSelectedNewFrequency(Number(event.target.value));
                      }}
                      checked={option === String(selectedNewFrequency)}
                      shouldCapitalizeLabel={false}
                      customInfo={
                        <DeliveryFrequencyInfo
                          newDateValue={newDateValue}
                          option={option}
                          handleClick={() => {
                            setStep(CHANGE_FREQUENCY_STEP.calendar);
                          }}
                        />
                      }
                    />
                  )}
                />
              </Space>
            );
          })}
        </form>
        {!isDisabled && <DeliveryFrequencyRibbon />}
      </Modal.Content>
      <Modal.Footer>
        <div className={styles.button}>
          <Button
            fluid
            theme="new"
            component="button"
            type="submit"
            size={{ xxs: 'small', s: 'medium' }}
            variant="primary"
            onClick={handleSubmitInternal}
            disabled={isDisabled}>
            {isLoadingStatus ? (
              <Spinner color="brand-white" size={24} withOverlay={false} />
            ) : (
              t('editFrequency.saveButton')
            )}
          </Button>
        </div>
      </Modal.Footer>
    </>
  );
};

export default DeliveryFrequencyView;
